import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { publicApi } from "../lib/api";
import { Logo } from "../components/Logo";
import { WebJestersSiteLink } from "../components/WebJestersSiteLink";
import { PrivacyPolicy, TermsOfService, DataProcessingAddendum } from "../legal/documents";

const DOCS = {
    "/privacy": { title: "Privacy Policy", Body: PrivacyPolicy, testid: "legal-privacy" },
    "/terms": { title: "Terms of Service", Body: TermsOfService, testid: "legal-terms" },
    "/dpa": { title: "Data Processing Addendum", Body: DataProcessingAddendum, testid: "legal-dpa" },
};

const FALLBACK = {
    entity_name: "WebJesters",
    mailing_address: "Arizona, USA",
    privacy_email: "privacy@webjesters.com",
    venue: "Maricopa County, Arizona",
    terms_version: "2026-06-01",
};

export default function Legal() {
    const { pathname } = useLocation();
    const doc = DOCS[pathname] || DOCS["/privacy"];
    const [info, setInfo] = useState(FALLBACK);

    useEffect(() => {
        publicApi.get("/public/legal").then((r) => setInfo({ ...FALLBACK, ...r.data })).catch(() => {});
        window.scrollTo(0, 0);
    }, [pathname]);

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-zinc-200" data-testid={doc.testid}>
            <header className="min-h-16 border-b border-zinc-900 flex flex-wrap items-center justify-between gap-4 px-6 py-4 lg:px-10">
                <Logo />
                <nav className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[12.5px] text-zinc-500" data-testid="legal-nav">
                    {Object.entries(DOCS).map(([path, d]) => (
                        <Link
                            key={path}
                            to={path}
                            data-testid={`legal-nav-${path.slice(1)}`}
                            className={path === pathname ? "text-zinc-100" : "hover:text-zinc-200 transition-colors"}
                        >
                            {d.title}
                        </Link>
                    ))}
                </nav>
            </header>

            <main className="max-w-3xl mx-auto px-6 lg:px-10 py-14">
                <Link
                    to="/login"
                    data-testid="legal-back-link"
                    className="inline-flex items-center gap-1.5 text-[12.5px] text-zinc-500 hover:text-zinc-200 transition-colors"
                >
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
                </Link>
                <p className="mt-8 text-[11.5px] uppercase tracking-[0.18em] text-zinc-500">
                    {info.entity_name} · Effective {info.terms_version}
                </p>
                <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-50" data-testid="legal-title">
                    {doc.title}
                </h1>
                <article className="legal-doc mt-10 space-y-8 text-[15px] leading-relaxed text-zinc-300">
                    <doc.Body info={info} />
                </article>
                <footer data-testid="legal-footer" className="mt-16 pt-6 border-t border-zinc-900 text-[12px] text-zinc-500">
                    Questions? Email{" "}
                    <a href={`mailto:${info.privacy_email}`} data-testid="legal-footer-email-link" className="text-zinc-300 hover:text-white">
                        {info.privacy_email}
                    </a>
                    . <WebJestersSiteLink testid="legal-webjesters-link">{info.entity_name}</WebJestersSiteLink> · {info.mailing_address}
                </footer>
            </main>
        </div>
    );
}
